import { useState } from "react";
import { CurrencyDollar } from "@phosphor-icons/react";
import { api, formatApiError, money } from "../lib/api";
import { toast } from "sonner";
import EditManualPaymentModal from "./EditManualPaymentModal";

const METHOD = { cash: "Cash", card_manual: "Card", card: "Card", other: "Other", tyro: "Tyro" };

export default function OverpaidPaymentsCard() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const scan = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sales-audit/non-cash-overpaid");
      setRows(data.items || []);
      if (!data.items?.length) toast.success("No non-cash overpayments found.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <section className="rounded-2xl border border-[#E5E7EB] bg-white overflow-hidden" data-testid="overpaid-payments-card">
      <div className="p-6 border-b border-[#E5E7EB] flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><CurrencyDollar size={18} weight="duotone" /></div>
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">PAYMENT RECORD CLEANUP</div>
          <div className="font-heading font-bold text-[15px] text-slate-900 tracking-tight mt-0.5">Fix card / Tyro / Other payments recorded above the total</div>
          <div className="text-[12px] text-slate-500 mt-0.5">Only cash can give change. These invoices show more paid than charged — edit the payment to the real amount and method (admin PIN).</div>
        </div>
        <button onClick={scan} disabled={loading} data-testid="scan-overpaid-btn" className="h-10 px-4 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[12px] font-bold disabled:opacity-50 shrink-0">
          {loading ? "Scanning…" : "Scan"}
        </button>
      </div>
      {rows && rows.length > 0 && (
        <div className="divide-y divide-slate-100" data-testid="overpaid-list">
          {rows.map((r) => (
            <div key={r.sale_id} className="px-6 py-4 flex flex-wrap items-center gap-3" data-testid={`overpaid-row-${r.sale_number}`}>
              <div className="flex-1 min-w-[220px]">
                <div className="text-[14px] font-bold text-slate-900">{r.sale_number} <span className="font-medium text-slate-500">· {r.ticket_number} · {r.customer_name || "Walk-in"}</span></div>
                <div className="text-[12px] text-slate-500">Total ${money(r.total)} · recorded ${money(r.amount_tendered)} · <span className="text-rose-600 font-semibold">+${money(r.overpaid)}</span> · {r.cashier_name} · {(r.created_at || "").slice(0, 10)}</div>
              </div>
              {r.payments.map((p) => (
                <button key={p.payment_id || p.payment_index} disabled={!p.editable} onClick={() => setEditing({ row: r, payment: p })} data-testid={`overpaid-edit-${r.sale_number}-${p.payment_index}`} className="h-9 px-3 rounded-lg border border-blue-300 text-[12px] font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-40">
                  Edit {METHOD[p.method] || p.method} ${money(p.amount)}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
      {editing && (
        <EditManualPaymentModal
          saleId={editing.row.sale_id}
          payment={editing.payment}
          maxAmount={Math.max(0, editing.row.total - editing.row.payments.filter((p) => p !== editing.payment).reduce((a, p) => a + Number(p.amount || 0), 0))}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); scan(); }}
        />
      )}
    </section>
  );
}
